// Lead-source capture for the ActiveCampaign hidden field.
//
// Strategy:
//   1. captureLeadSource()  — called once at app boot in main.jsx. Reads URL
//      query params (utm_source, li, then full query string fallback) and
//      persists to localStorage. Persistence is needed because the AC form is
//      opened from a modal that mounts later — the URL params may already be
//      stripped by then if the user navigated client-side.
//   2. attachLeadSourceFiller(root, fieldName) — called from WebinarFormModal
//      after the AC embed script is injected. A MutationObserver waits for
//      the AC script to render the form HTML into `root`, then writes the
//      captured value into the named hidden input.
//
// The field name is configured in WebinarPage.jsx (AC_LEAD_SOURCE_FIELD).
// In dev mode, a one-shot warning fires if the target field isn't present in
// the rendered form — the warning lists the field names that ARE present so
// the misconfiguration is obvious.

const STORAGE_KEY = 'aa_lead_source';

function readSourceFromUrl() {
  if (typeof window === 'undefined' || !window.location.search) return '';
  try {
    const params = new URLSearchParams(window.location.search);
    // Primary: utm_source present → assume a real attributed click (Meta,
    // Google Ads, newsletter, …) and concat the full Meta-style chain into
    // a readable key=value string. Empty positions are dropped so non-Meta
    // sources with only utm_source stay clean. Format:
    //   utm_source=ig | utm_campaign=Webinar-Juni | utm_term=Cold-Founders | utm_content=Cursor-Hook | placement=feed
    // The key=value shape makes lead exports self-documenting — you can see
    // WHICH parameter carries a given value without having to remember the
    // positional convention. Also survives future param additions without
    // breaking downstream lead-source-based segmentation.
    const utm = params.get('utm_source');
    if (utm) {
      const entries = [
        ['utm_source',   utm],
        ['utm_campaign', params.get('utm_campaign')], // Meta template: campaign name / id
        ['utm_term',     params.get('utm_term')],     // Meta template: adset name / id
        ['utm_content',  params.get('utm_content')],  // Meta template: ad name / id
        ['placement',    params.get('placement')],    // Meta template: feed / story / reels / ...
      ];
      return entries.filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(' | ');
    }
    // Secondary: short LinkedIn-style override.
    const li = params.get('li');
    if (li) return li;
    // Fallback: ALL params joined as readable key=value pairs (URL-decoded,
    // joined with " | ") — so fbclid / gclid etc. don't get lost when no
    // utm_source is set.
    const parts = [];
    for (const [key, value] of params) {
      parts.push(value ? `${key}=${value}` : key);
    }
    return parts.join(' | ');
  } catch {
    return '';
  }
}

export function captureLeadSource() {
  const source = readSourceFromUrl();
  if (!source) return;
  try {
    localStorage.setItem(STORAGE_KEY, source);
  } catch {
    // localStorage may be disabled (private mode, storage quota). Silent.
  }
}

export function getLeadSource() {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function attachLeadSourceFiller(root, fieldName) {
  if (!root || !fieldName) return () => {};
  const source = getLeadSource();

  let filled = false;
  let warnedMissing = false;
  let observer = null;
  let timeoutId = null;

  const tryFill = () => {
    if (filled) return;

    // Wait for AC to render the form — no hidden inputs yet means the script
    // hasn't injected the form HTML.
    const allHidden = root.querySelectorAll('input[type="hidden"]');
    if (allHidden.length === 0) return;

    const input = root.querySelector(`input[name="${fieldName}"]`);

    if (!input) {
      // Form rendered, but the configured hidden field isn't there.
      // One-shot dev warning with the list of fields that ARE present so the
      // misconfiguration is obvious without DOM inspection.
      if (import.meta.env.DEV && !warnedMissing) {
        warnedMissing = true;
        const names = Array.from(allHidden).map(i => i.name).filter(Boolean);
        // eslint-disable-next-line no-console
        console.warn(
          `[lead-source] Hidden field "${fieldName}" not found in rendered AC form. ` +
          `Update AC_LEAD_SOURCE_FIELD in WebinarPage.jsx. Available hidden fields:`,
          names,
        );
      }
      return;
    }

    if (!source) return;

    input.value = source;
    // Dispatch input event so any AC-side listeners pick up the value.
    input.dispatchEvent(new Event('input', { bubbles: true }));
    filled = true;
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`Leadsource: ${source}`);
    }
  };

  // Initial attempt — form may already be rendered if the modal reopens.
  tryFill();
  if (filled) return () => {};

  observer = new MutationObserver(() => {
    tryFill();
    if (filled && observer) {
      observer.disconnect();
      observer = null;
    }
  });
  observer.observe(root, { childList: true, subtree: true });

  // Safety net: stop watching after 10 s. AC's auto-scan is usually < 1 s.
  timeoutId = setTimeout(() => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  }, 10000);

  return () => {
    if (observer) observer.disconnect();
    if (timeoutId) clearTimeout(timeoutId);
  };
}
